'use client';

import { useState } from 'react';
import { Container } from '@/atoms/Container/Container';
import { Typography } from '@/atoms/Typography/Typography';
import { TAG_INPUT_WIDTH_DEFAULT } from '@/config/tags';
import { useEnrichedTags } from '@/hooks/useEnrichedTags/useEnrichedTags';
import { useRequireAuth } from '@/hooks/useRequireAuth/useRequireAuth';
import { useResourceTags } from '@/hooks/useResourceTags/useResourceTags';
import { generateRandomColor } from '@/libs/utils/utils';
import { PostTag } from '@/molecules/PostTag/PostTag';
import { PostTagAddButton } from '@/molecules/PostTagAddButton/PostTagAddButton';
import { PostTagPopoverWrapper } from '@/molecules/PostTagPopoverWrapper/PostTagPopoverWrapper';
import type { TagWithAvatars } from '@/molecules/TaggedItem/TaggedItem.types';
import { TagInput } from '@/molecules/TagInput/TagInput';
import { TagInputToggle } from '@/molecules/TagInputToggle/TagInputToggle';

interface ResourceTagBarProps {
  /** The external resource's link exactly as written: what is tagged. */
  uri: string;
  /**
   * Names what is tagged in plain words ("Tags on this video"), first in the row; absent when the
   * label already shows in the action row above.
   */
  label?: string;
}

/** Names what the tags are on ("Tags on this city"), in the app's small caps label style. */
export function ResourceTagLabel({ label }: { label: string }) {
  return (
    <Typography
      as="span"
      overrideDefaults
      data-testid="resource-tag-bar-label"
      className="min-w-0 text-xs font-medium tracking-wide text-muted-foreground uppercase"
    >
      {label}
    </Typography>
  );
}

/**
 * The tags people put on an external resource, in one wrapping row: the label when given, the
 * app's tag chips (label and count, the viewer's own highlighted, tagger avatars on hover), each
 * toggling the viewer's tag, then "+" to add one. With no tags yet only "+" (and the label) show.
 * Signed out, every write asks to sign in.
 */
export function ResourceTagBar({ uri, label }: ResourceTagBarProps) {
  const { isAuthenticated, requireAuth } = useRequireAuth();
  const { tags, addTag, toggleTag } = useResourceTags(uri);
  const [isAdding, setIsAdding] = useState(false);
  const { enrichedTags } = useEnrichedTags(
    tags.map((tag): TagWithAvatars => ({ ...tag, taggers: tag.taggers.map((id) => ({ id })) })),
  );
  const viewerTags = tags.filter((tag) => tag.relationship);

  const handleAdd = async (value: string) => {
    const result = await addTag(value);
    if (result.success) setIsAdding(false);
    return result;
  };

  return (
    <Container overrideDefaults data-testid="resource-tag-bar" className="flex min-w-0 flex-wrap items-center gap-2">
      {label && <ResourceTagLabel label={label} />}
      {enrichedTags.map((tag) => (
        <PostTagPopoverWrapper
          key={tag.label}
          taggers={tag.taggers}
          taggersCount={tag.taggers_count}
          postId={null}
          tagLabel={tag.label}
        >
          <PostTag
            label={tag.label}
            count={tag.taggers_count}
            color={generateRandomColor(tag.label)}
            selected={tag.relationship}
            onClick={() => requireAuth(() => void toggleTag(tag))}
          />
        </PostTagPopoverWrapper>
      ))}
      <TagInputToggle
        showInput={isAdding}
        widthByState={{ input: TAG_INPUT_WIDTH_DEFAULT, addButton: 34 }}
        inputContent={
          <TagInput
            onTagAdd={handleAdd}
            existingTags={tags}
            viewerTags={viewerTags}
            showCloseButton
            onClose={() => setIsAdding(false)}
            onBlur={() => setIsAdding(false)}
            disabled={!isAuthenticated}
            currentTagsCount={tags.length}
            enableApiSuggestions={isAuthenticated}
            excludeFromApiSuggestions={tags.map((tag) => tag.label)}
            addOnSuggestionClick
            autoFocus
            containerVariant="plain"
            className="w-full shrink-0"
          />
        }
        addButtonContent={<PostTagAddButton variant="plain" onClick={() => requireAuth(() => setIsAdding(true))} />}
      />
    </Container>
  );
}
